// TanStack Query hooks for the Incidents module. An incident is a security
// occurrence (trespass, theft, use of force, …) that moves through an approval
// lifecycle (draft → submitted → under_review → approved | rejected). Each
// incident owns an append-only event timeline and a soft-deletable list of
// involved parties. Mirrors packages/api/src/routes/incidents.ts.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateIncidentEventInput,
  CreateIncidentInput,
  CreateIncidentPartyInput,
  IncidentCategory,
  IncidentEventResponse,
  IncidentEventsListResponse,
  IncidentPartiesListResponse,
  IncidentPartyResponse,
  IncidentResponse,
  IncidentSeverity,
  IncidentsListResponse,
  IncidentTransitionInput,
  ReportStatus,
  UpdateIncidentInput,
  UpdateIncidentPartyInput,
} from '@dsr/shared-types';
import { api } from '../client';

// ---------- Query keys ----------

const KEY = 'incidents';

export const incidentKeys = {
  all: [KEY] as const,
  lists: () => [KEY, 'list'] as const,
  list: (filters: IncidentFilters) => [KEY, 'list', { ...filters }] as const,
  detail: (id: number) => [KEY, 'detail', id] as const,
  events: (id: number) => [KEY, 'detail', id, 'events'] as const,
  parties: (id: number) => [KEY, 'detail', id, 'parties'] as const,
};

// =============================================================================
// Incidents
// =============================================================================

export interface IncidentFilters {
  status?: ReportStatus;
  severity?: IncidentSeverity;
  category?: IncidentCategory;
  site_id?: number;
  assignment_id?: number;
  principal_id?: number;
}

export function useIncidents(filters: IncidentFilters = {}) {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.severity) params.set('severity', filters.severity);
  if (filters.category) params.set('category', filters.category);
  if (filters.site_id !== undefined) params.set('site_id', String(filters.site_id));
  if (filters.assignment_id !== undefined) {
    params.set('assignment_id', String(filters.assignment_id));
  }
  if (filters.principal_id !== undefined) {
    params.set('principal_id', String(filters.principal_id));
  }
  const qs = params.toString();
  const url = qs.length > 0 ? `/api/incidents?${qs}` : '/api/incidents';
  return useQuery({
    queryKey: incidentKeys.list(filters),
    queryFn: ({ signal }) => api.get<IncidentsListResponse>(url, signal),
  });
}

export function useIncident(id: number | null) {
  return useQuery({
    queryKey: incidentKeys.detail(id ?? 0),
    enabled: id !== null && id > 0,
    queryFn: ({ signal }) => api.get<IncidentResponse>(`/api/incidents/${id}`, signal),
  });
}

export function useCreateIncident() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateIncidentInput) =>
      api.post<IncidentResponse>('/api/incidents', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: incidentKeys.lists() });
    },
  });
}

export interface UpdateIncidentArgs extends UpdateIncidentInput {
  id: number;
}

export function useUpdateIncident() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: UpdateIncidentArgs) =>
      api.patch<IncidentResponse>(`/api/incidents/${id}`, patch),
    onSuccess: (_data, { id }) => {
      void qc.invalidateQueries({ queryKey: incidentKeys.lists() });
      void qc.invalidateQueries({ queryKey: incidentKeys.detail(id) });
    },
  });
}

/**
 * Move an incident through its approval lifecycle. Gated server-side on
 * incident.review (distinct from incident.write). The transition appends an
 * immutable incident_event, so the event timeline is invalidated too.
 */
export interface TransitionIncidentArgs extends IncidentTransitionInput {
  id: number;
}

export function useTransitionIncident() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: TransitionIncidentArgs) =>
      api.post<IncidentResponse>(`/api/incidents/${id}/transition`, body),
    onSuccess: (_data, { id }) => {
      void qc.invalidateQueries({ queryKey: incidentKeys.lists() });
      void qc.invalidateQueries({ queryKey: incidentKeys.detail(id) });
      void qc.invalidateQueries({ queryKey: incidentKeys.events(id) });
    },
  });
}

export function useDeleteIncident() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/incidents/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: incidentKeys.lists() });
    },
  });
}

// =============================================================================
// Incident events (append-only timeline)
// =============================================================================

export function useIncidentEvents(incidentId: number | null) {
  return useQuery({
    queryKey: incidentKeys.events(incidentId ?? 0),
    enabled: incidentId !== null && incidentId > 0,
    queryFn: ({ signal }) =>
      api.get<IncidentEventsListResponse>(`/api/incidents/${incidentId}/events`, signal),
  });
}

export interface CreateIncidentEventArgs extends CreateIncidentEventInput {
  incidentId: number;
}

export function useCreateIncidentEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ incidentId, ...input }: CreateIncidentEventArgs) =>
      api.post<IncidentEventResponse>(`/api/incidents/${incidentId}/events`, input),
    onSuccess: (_data, { incidentId }) => {
      void qc.invalidateQueries({ queryKey: incidentKeys.events(incidentId) });
    },
  });
}

// =============================================================================
// Incident parties
// =============================================================================

export function useIncidentParties(incidentId: number | null) {
  return useQuery({
    queryKey: incidentKeys.parties(incidentId ?? 0),
    enabled: incidentId !== null && incidentId > 0,
    queryFn: ({ signal }) =>
      api.get<IncidentPartiesListResponse>(`/api/incidents/${incidentId}/parties`, signal),
  });
}

export interface CreateIncidentPartyArgs extends CreateIncidentPartyInput {
  incidentId: number;
}

export function useCreateIncidentParty() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ incidentId, ...input }: CreateIncidentPartyArgs) =>
      api.post<IncidentPartyResponse>(`/api/incidents/${incidentId}/parties`, input),
    onSuccess: (_data, { incidentId }) => {
      void qc.invalidateQueries({ queryKey: incidentKeys.parties(incidentId) });
    },
  });
}

export interface UpdateIncidentPartyArgs extends UpdateIncidentPartyInput {
  incidentId: number;
  partyId: number;
}

export function useUpdateIncidentParty() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ incidentId, partyId, ...patch }: UpdateIncidentPartyArgs) =>
      api.patch<IncidentPartyResponse>(
        `/api/incidents/${incidentId}/parties/${partyId}`,
        patch,
      ),
    onSuccess: (_data, { incidentId }) => {
      void qc.invalidateQueries({ queryKey: incidentKeys.parties(incidentId) });
    },
  });
}

export interface DeleteIncidentPartyArgs {
  incidentId: number;
  partyId: number;
}

export function useDeleteIncidentParty() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ incidentId, partyId }: DeleteIncidentPartyArgs) =>
      api.delete<void>(`/api/incidents/${incidentId}/parties/${partyId}`),
    onSuccess: (_data, { incidentId }) => {
      void qc.invalidateQueries({ queryKey: incidentKeys.parties(incidentId) });
    },
  });
}
